"use client";

import { useState, useMemo } from "react";
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  subDays,
  addDays,
  parseISO,
  isSameDay,
} from "date-fns";
import { ChevronLeft, ChevronRight, Edit2, Trash, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Skeleton } from "src/components/ui/skeleton";
import { useLanguage } from "../../context/LanguageContext";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

export default function TimeSelectorTeacher({
  loading,
  data,
  openDeleteDialog,
  handleEdit,
}) {
  const { language } = useLanguage();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("day");
  const [selectedAvailability, setSelectedAvailability] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Sample teacher availability data
  const availabilityData = [
    {
      availability: {
        id: 5,
        teacher_id: 4,
        day_of_week: "Friday",
        start_time: "10:04:00",
        end_time: "11:04:00",
        is_recurring: 0,
        created_at: "2025-04-13T14:50:39.000000Z",
        updated_at: "2025-04-13T14:55:38.000000Z",
        start_date: "2025-04-15",
        end_date: "2025-04-30",
      },
      start_time: "10:04:00",
      end_time: "11:03:00",
      date: "2025-04-15",
      status: "Booked",
      course_name: {
        ar: "لوازم الفن",
        en: "Art Supplies",
      },
      student_name: "System studenttt",
    },
    {
      availability: {
        id: 5,
        teacher_id: 4,
        day_of_week: "Friday",
        start_time: "10:04:00",
        end_time: "11:04:00",
        is_recurring: 0,
        created_at: "2025-04-13T14:50:39.000000Z",
        updated_at: "2025-04-13T14:55:38.000000Z",
        start_date: "2025-04-15",
        end_date: "2025-04-30",
      },
      start_time: "11:03:00",
      end_time: "11:04:00",
      status: "Unbooked",
    },
    {
      availability: {
        id: 4,
        teacher_id: 4,
        day_of_week: "Monday",
        start_time: "13:04:00",
        end_time: "23:04:00",
        is_recurring: 0,
        created_at: "2025-04-13T14:28:17.000000Z",
        updated_at: "2025-04-13T14:40:20.000000Z",
        start_date: "2025-04-13",
        end_date: "2025-04-15",
      },
      start_time: "13:04:00",
      end_time: "14:03:00",
      date: "2025-04-13",
      status: "Booked",
      course_name: {
        en: "Graphic Design Tools",
        ar: "أدوات التصميم الجرافيكي",
      },
      student_name: "System studenttt",
    },
    {
      availability: {
        id: 4,
        teacher_id: 4,
        day_of_week: "Monday",
        start_time: "13:04:00",
        end_time: "23:04:00",
        is_recurring: 0,
        created_at: "2025-04-13T14:28:17.000000Z",
        updated_at: "2025-04-13T14:40:20.000000Z",
        start_date: "2025-04-13",
        end_date: "2025-04-15",
      },
      start_time: "14:03:00",
      end_time: "23:04:00",
      status: "Unbooked",
    },
  ];

  // Full day time slots from 1 AM to 12 AM (midnight) in 24-hour format
  const fullDayTimeSlots = [
    "01:00",
    "02:00",
    "03:00",
    "04:00",
    "05:00",
    "06:00",
    "07:00",
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
    "23:00",
    "00:00",
  ];

  // Get days of current week
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
  const daysOfWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Helper function to check if day of week matches
  const isDayOfWeekMatch = (date, dayOfWeek) => {
    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayIndex = date.getDay();
    return dayNames[dayIndex] === dayOfWeek;
  };

  // Check if a time slot has availability
  const getAvailabilityForTimeSlot = (date, timeSlot) => {
    const [hours] = timeSlot.split(":");
    const hourNum = Number.parseInt(hours, 10);

    // BOOKED
    const bookedSlot = data.find((slot) => {
      if (slot.status !== "Booked" || !slot.date) return false;
      const slotDate = parseISO(slot.date);
      if (!isSameDay(date, slotDate)) return false;

      const startH = +slot.start_time.split(":")[0];
      const endH = +slot.end_time.split(":")[0] + 1;
      return hourNum >= startH && hourNum < endH;
    });
    if (bookedSlot) return bookedSlot;

    // UNBOOKED
    const unbookedSlot = data.find((slot) => {
      if (slot.status !== "Unbooked") return false;
      const availStart = parseISO(slot.availability?.start_date);
      const availEnd = parseISO(slot.availability?.end_date);
      if (date < availStart || date > availEnd) return false;
      if (!isDayOfWeekMatch(date, slot.availability?.day_of_week)) return false;

      const startH = +slot.start_time.split(":")[0];
      const endH = +slot.end_time.split(":")[0] + 1;
      return hourNum >= startH && hourNum < endH;
    });
    if (unbookedSlot) return unbookedSlot;

    // DEFAULT
    const defaultSlot = data.find((slot) => {
      if (slot.status === "Unbooked" || slot.status === "Booked") return false;
      const availStart = parseISO(slot.start_date);
      const availEnd = parseISO(slot.end_date);
      if (date < availStart || date > availEnd) return false;
      if (!isDayOfWeekMatch(date, slot.day_of_week)) return false;

      const startH = +slot.start_time.split(":")[0];
      const endH = +slot.end_time.split(":")[0] + 1;
      return hourNum >= startH && hourNum < endH;
    });
    if (defaultSlot) return defaultSlot;
  };

  // Function to get availability for a specific day
  const getAvailabilityForDay = (date) => {
    return fullDayTimeSlots.map((time) => {
      const availabilitySlot = getAvailabilityForTimeSlot(date, time);
      return {
        time,
        selected: !!availabilitySlot,
        availabilitySlot,
      };
    });
  };

  // Week availability
  const weekAvailability = useMemo(() => {
    const weekData = {};

    daysOfWeek.forEach((day) => {
      const dayStr = format(day, "yyyy-MM-dd");
      weekData[dayStr] = getAvailabilityForDay(day);
    });

    return weekData;
  }, [daysOfWeek, data]);

  // Sample time slots for a single day
  const dayTimeSlots = useMemo(() => {
    return getAvailabilityForDay(selectedDate);
  }, [selectedDate, data]);

  const handlePrevious = () => {
    if (view === "day") {
      setSelectedDate(subDays(selectedDate, 1));
    } else {
      setSelectedDate(subDays(selectedDate, 7));
    }
  };

  const handleNext = () => {
    if (view === "day") {
      setSelectedDate(addDays(selectedDate, 1));
    } else {
      setSelectedDate(addDays(selectedDate, 7));
    }
  };

  const handleTimeSlotClick = (slot) => {
    if (slot.availabilitySlot) {
      setSelectedAvailability(slot.availabilitySlot);
      setDialogOpen(true);
    }
  };

  // Format "HH:mm": drop ":00" if minutes are zero
  const formatHM = (t) => {
    const hm = t?.slice(0, 5);
    return hm;
  };

  // Skeleton loaders
  const DayViewSkeleton = () => (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
      {Array(12)
        .fill(0)
        .map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-md" />
        ))}
    </div>
  );

  const WeekViewSkeleton = () => (
    <div className="overflow-x-auto">
      <div className="min-w-full">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {Array(7)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-md" />
            ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array(7)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="space-y-1">
                {Array(8)
                  .fill(0)
                  .map((_, j) => (
                    <Skeleton key={j} className="h-8 w-full rounded-md" />
                  ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  // Helper function to get the display date for an availability slot
  const getDisplayDate = (slot) => {
    if (slot.date) {
      return format(parseISO(slot.date), "MMMM d, yyyy");
    } else {
      // For unbooked slots without a date, show the availability date range
      return `${format(
        parseISO(slot.availability?.start_date),
        "MMM d"
      )} - ${format(parseISO(slot.availability?.end_date), "MMM d, yyyy")}`;
    }
  };

  return (
    <div className="w-full text-primary overflow-auto">
      <div className={cn(view === "week" && "min-w-[650px]")}>
        <div
          className={`flex justify-between items-center flex-col sm:flex-row gap-2 mb-2 p-3 bg-primary dark:bg-gray-100 text-background rounded-t-xl`}
        >
          <div className="inline-flex bg-background rounded-xl overflow-hidden">
            {["week", "day"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-4 py-2 text-sm ${
                  view === v ? "bg-blue-600 text-white" : "text-primary"
                }`}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={handlePrevious}>
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="text-sm">
              {view === "day"
                ? format(selectedDate, "EEEE, MMMM d, yyyy")
                : `${format(weekStart, "MMM d")} - ${format(
                    weekEnd,
                    "MMM d, yyyy"
                  )}`}
            </span>
            <button onClick={handleNext}>
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        view === "day" ? (
          <DayViewSkeleton />
        ) : (
          <WeekViewSkeleton />
        )
      ) : (
        <>
          {/* --- Day View --- */}
          {view === "day" && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {(() => {
                const groups = [];
                let curr = null,
                  buf = [];

                dayTimeSlots.forEach((slot) => {
                  if (slot.availabilitySlot) {
                    if (
                      !curr ||
                      curr.availability?.id !==
                        slot.availabilitySlot.availability?.id
                    ) {
                      if (buf.length) {
                        groups.push({ availabilitySlot: curr, slots: buf });
                      }
                      buf = [];
                      curr = slot.availabilitySlot;
                    }
                    buf.push(slot);
                  } else {
                    if (buf.length) {
                      groups.push({ availabilitySlot: curr, slots: buf });
                      buf = [];
                      curr = null;
                    }
                    groups.push({ availabilitySlot: null, slots: [slot] });
                  }
                });
                if (buf.length)
                  groups.push({ availabilitySlot: curr, slots: buf });

                return groups.map((g, i) =>
                  g.availabilitySlot ? (
                    <div
                      key={i}
                      className={`col-span-${g.slots.length} p-3 rounded-md ${
                        g.availabilitySlot.status === "Booked"
                          ? "bg-gray-400"
                          : "bg-green-600"
                      } text-white cursor-pointer flex flex-col justify-center items-center`}
                      style={{ gridColumn: `span ${g.slots.length}` }}
                      onClick={() => handleTimeSlotClick(g.slots[0])}
                    >
                      <div className="text-sm font-medium">
                        {`${formatHM(
                          g.availabilitySlot.start_time
                        )} - ${formatHM(g.availabilitySlot.end_time)}`}
                      </div>
                      <div className="text-xs mt-1">
                        {g.availabilitySlot.status}
                      </div>
                    </div>
                  ) : (
                    <div
                      key={i}
                      className="p-3 rounded-md bg-gray-100 text-black flex items-center justify-center"
                    >
                      <span className="text-sm font-medium">
                        {g.slots[0].time}
                      </span>
                    </div>
                  )
                );
              })()}
            </div>
          )}

          {/* --- Week View --- */}
          {view === "week" && (
            <div className="min-w-[650px]">
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {daysOfWeek.map((d) => (
                      <div
                        key={format(d, "yyyy-MM-dd")}
                        className="text-center p-2 whitespace-nowrap"
                      >
                        <div className="font-medium">{format(d, "EEE")}</div>
                        <div className="text-sm text-gray-400">
                          {format(d, "MMM d")}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Slots grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {daysOfWeek.map((d) => {
                      const dayStr = format(d, "yyyy-MM-dd");
                      const slots = weekAvailability[dayStr] || [];

                      // group same as day view
                      const groups = [];
                      let curr = null,
                        buf = [];
                      slots.forEach((slot) => {
                        if (slot.availabilitySlot) {
                          if (
                            !curr ||
                            curr.availability?.id !==
                              slot.availabilitySlot.availability?.id
                          ) {
                            if (buf.length)
                              groups.push({
                                availabilitySlot: curr,
                                slots: buf,
                              });
                            buf = [];
                            curr = slot.availabilitySlot;
                          }
                          buf.push(slot);
                        } else {
                          if (buf.length) {
                            groups.push({ availabilitySlot: curr, slots: buf });
                            buf = [];
                            curr = null;
                          }
                          groups.push({
                            availabilitySlot: null,
                            slots: [slot],
                          });
                        }
                      });
                      if (buf.length)
                        groups.push({ availabilitySlot: curr, slots: buf });

                      return (
                        <div key={dayStr} className="space-y-1">
                          {groups.map((g, idx) =>
                            g.availabilitySlot ? (
                              <div
                                key={idx}
                                className={`p-2 rounded-md ${
                                  g.availabilitySlot.status === "Booked"
                                    ? "bg-gray-400"
                                    : "bg-green-600"
                                } text-white cursor-pointer flex flex-col justify-center items-center`}
                                style={{
                                  height: `${g.slots.length * 27.6}px`,
                                }}
                                onClick={() => handleTimeSlotClick(g.slots[0])}
                              >
                                <div className="text-xs font-medium">
                                  {`${formatHM(
                                    g.availabilitySlot.start_time
                                  )} - ${formatHM(
                                    g.availabilitySlot.end_time
                                  )}`}
                                </div>
                                <div className="text-xs">
                                  {g.availabilitySlot.status}
                                </div>
                              </div>
                            ) : (
                              <div
                                key={idx}
                                className="p-1 rounded-md bg-gray-100 text-black flex items-center justify-center"
                              >
                                <span className="text-xs font-medium">
                                  {g.slots[0].time}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* --- Availability Dialog --- */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-md"
          dir={language === "en" ? "ltr" : "rtl"}
        >
          <DialogHeader>
            <DialogTitle>Availability Details</DialogTitle>
          </DialogHeader>
          {selectedAvailability?.status ? (
            <div className="p-4" dir={language === "en" ? "ltr" : "rtl"}>
              {selectedAvailability?.status !== "Unbooked" && (
                <div className="">
                  <div className="mb-2">
                    <span className="font-bold">Student name: </span>
                    <span>{selectedAvailability?.student_name}</span>
                  </div>
                  <div className="mb-2">
                    <span className="font-bold">Course name: </span>
                    <span>
                      {selectedAvailability?.course_name[language] ||
                        selectedAvailability?.course_name.en}
                    </span>
                  </div>
                </div>
              )}

              <div className="mb-2">
                <span className="font-bold">Day: </span>
                <span>{selectedAvailability?.availability?.day_of_week}</span>
              </div>
              <div className="mb-2">
                <span className="font-bold">Date: </span>
                <span>
                  {selectedAvailability?.date
                    ? format(
                        parseISO(selectedAvailability?.date),
                        "MMMM d, yyyy"
                      )
                    : `${format(
                        parseISO(
                          selectedAvailability?.availability?.start_date
                        ),
                        "MMM d"
                      )} - ${format(
                        parseISO(selectedAvailability?.availability?.end_date),
                        "MMM d, yyyy"
                      )}`}
                </span>
              </div>
              <div className="mb-2">
                <span className="font-bold">Time: </span>
                <span>
                  {`${formatHM(selectedAvailability?.start_time)} - ${formatHM(
                    selectedAvailability?.end_time
                  )}`}
                </span>
              </div>
              <div>
                <span className="font-bold">Status: </span>
                <span
                  className={
                    selectedAvailability?.status === "Unbooked"
                      ? "text-green-600"
                      : "text-gray-500"
                  }
                >
                  {selectedAvailability?.status}
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="">
                <span className="font-bold">Day: </span>
                <span>{selectedAvailability?.day_of_week}</span>
              </div>
              <div className="">
                <span className="font-bold">Date: </span>
                {selectedAvailability?.start_date} -{" "}
                {selectedAvailability?.end_date}
              </div>
              <div className="">
                <span className="font-bold">Time: </span>
                <span>
                  {`${formatHM(selectedAvailability?.start_time)} - ${formatHM(
                    selectedAvailability?.end_time
                  )}`}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 pt-4">
                <Button
                  className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-md"
                  onClick={(e) => {
                    openDeleteDialog(selectedAvailability.id, e);
                    setDialogOpen(false);
                  }}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-md"
                  onClick={() => {
                    handleEdit(selectedAvailability);
                    setDialogOpen(false);
                  }}
                >
                  <Edit2 className="h-4 w-4" /> Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
